import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AppContext } from '../../app.context';
import { useTour } from '../../TourContext.jsx';
import Header from '../../components/Header2';
import styled from 'styled-components';
import closeicon from '../../static/icons/closeicon-gray.svg';
import { Box, Typography } from '@mui/material';
import righticon from '../../static/icons/right-icon.svg';
import checkboxicon from '../../static/icons/checkbox-icon.svg';
import { Title, SubText, TextContainer, ContentWrapper, ModalContainer, CloseIcon, ProgressContainer, HeaderWrapper, InnerWrapper, PersonaliseTitle, SectionTitle, Container } from './OnBoardingCompleteStyle';
import ProgressBar from '../ProgressBar/ProgressBar';
import SessionBookingPopup from '../SessionBooking/index.jsx';
import VerifyClassesPopup from '../VerifyClassesPopup';
import ClassChangeRequestModal from '../ClassChangeRequest';
import MarkingPreferencesPopup from '../../components/MarkingPreferencesPopup/index.jsx';
import completeicon from '../../static/icons/complete-icon.svg';
import PurpleBackgroundBtn from '../Buttons/PurpleBackgroundBtn/index.jsx';
import { toast } from 'react-toastify';
import Toast from '../../components/Toast';
import { saveSessionBooking, markOnboardingCompleted } from '../../service';
import TourExitWarning from '../../components-onboarding/TourExitWarning';

// This component is used to display the onboarding complete ( A list of onboarding tasks) popup

const OnboardingComplete = ({ onClose }) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const {
    onboardingTasks,
    setOnboardingTasks,
    showDraftFeedback,
    setShowDraftFeedback,
    setOnboardingProgress,
    launchTour,
    // setShowTeacherOnboarding,
  } = useTour();

  const [showSessionBooking, setShowSessionBooking] = useState(false);
  const [showVerifyClassesPopup, setShowVerifyClassesPopup] = useState(false);
  const [showClassChangeRequest, setShowClassChangeRequest] = useState(false);
  const [showMarkingPreferencesPopup, setShowMarkingPreferencesPopup] = useState(false);
  // Exit-warning queue. null = no warning showing; otherwise holds the
  // navigation callback to run after the user confirms "Leave tour."
  const [pendingExit, setPendingExit] = useState(null);
  const { setShowTeacherOnboarding } = useContext(AppContext);

  const completedTasks = onboardingTasks;
  const isPersonaliseCompleted = completedTasks.slice(0, 3).every(Boolean);

  const updateTaskCompletion = (index, isCompleted = true) => {
    const newCompletedTasks = [...completedTasks];
    newCompletedTasks[index] = isCompleted;
    setOnboardingTasks(newCompletedTasks);
    // Keep the Setup Progress ring in sync SYNCHRONOUSLY: rows like "View
    // class insights" tick their task and immediately close/unmount this
    // component, so the progress-sync useEffect below may never run with the
    // new value — leaving the sidebar ring stale (e.g. 88% after the last
    // task) until the checklist is reopened.
    setOnboardingProgress(
      Math.round((newCompletedTasks.filter(Boolean).length / 8) * 100)
    );
  };

  const completedCount = completedTasks.filter(t => t).length;
  const progressPercentage = Math.round((completedCount / 8) * 100);
  const remainingCount = 8 - completedCount;

  useEffect(() => {
    setOnboardingProgress(progressPercentage);
  }, [progressPercentage]);

  const menuItems = [
    "Book your live faculty onboarding session",
    "Confirm your classes have been set up correctly",
    "Add your marking preferences",
  ];

  const platformItems = [
    "View marking templates",
    "Submit a draft in practice mode",
    "Set up a task for a class",
    "Give feedback on a student response",
    "View class insights"
  ];


  // Persist "user dismissed the checklist" to the backend AND eagerly flip
  // the cached profile so the auto-show useEffect in App.jsx (which guards
  // on profile.hasCompletedOnboarding) doesn't immediately re-open the
  // modal during the in-flight PATCH window. Without the cache write the
  // auto-show effect re-runs as soon as onClose() flips showOnboardingComplete
  // to false: same render-cycle, cached profile.hasCompletedOnboarding is
  // still false, all other guards pass — modal re-opens with no user action.
  const persistDismissal = () => {
    // Capture server-truth completion BEFORE the in-session cache flip.
    const wasAlreadyCompleted = Boolean(
      queryClient.getQueryData(['profile'])?.hasCompletedOnboarding
    );
    // In-session suppression so the checklist doesn't immediately re-open.
    queryClient.setQueryData(['profile'], (old) => (
      old ? { ...old, hasCompletedOnboarding: true } : old
    ));
    // Persist completion for a genuine first-run dismissal; skip ONLY for an
    // already-finished teacher re-watching via the Help-Centre relaunch, so a
    // re-watch never re-writes their state. See TourGuard.handleLeaveTour.
    if (!wasAlreadyCompleted) {
      markOnboardingCompleted().catch((err) => {
        console.error('Failed to persist onboarding dismissal', err);
      });
    }
  };

  const exitToTasks = () => {
    persistDismissal();
    onClose();
    history.push("/tasks");
  };

  const exitToTaskCreation = () => {
    persistDismissal();
    onClose();
    history.push("/tasks/new");
  };

  // Close path (X icon AND "Skip all" button). When the user hasn't completed
  // every task, surface the TourExitWarning popup first so they don't lose
  // their setup state accidentally. Confirming the warning persists the
  // dismissal flag to the backend — the checklist won't auto-show on next
  // login. When every task is done, the warning is unnecessary noise; close
  // straight away.
  const handleOnClose = () => {
    const allDone = completedTasks.every(Boolean);
    if (allDone) {
      exitToTasks();
      return;
    }
    setPendingExit(() => exitToTasks);
  };

  // Removed auto-close effect to show the completion state
  // useEffect(() => {
  //   const allCompleted = onboardingTasks.every(Boolean);
  //   if (allCompleted) {
  //     handleOnClose();
  //   }
  // }, [onboardingTasks]);


  const ListItemRow = ({ text, onClick, isCompleted }) => (
    <Box
      // Completed rows stay clickable so the user can redo / re-watch a
      // tour or re-open a personalise popup. The visual "done" state
      // (green check, strikethrough, no chevron) signals it's been done
      // before — clicking it just relaunches the matching flow.
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: "1px solid #949494",
        borderRadius: "12px",
        padding: "14px 24px",
        cursor: "pointer",
        backgroundColor: "white",
        '&:hover': {
          backgroundColor: '#FBF7FE',
          border: "1px solid #7200E0",
          color: "#7200E0",
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {isCompleted ? (
          <Box sx={{
            width: '24px',
            height: '24px',
            backgroundColor: '#4CAF50',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <img src={completeicon} alt="complete-icon" />
          </Box>
        ) : (
          <img src={checkboxicon} alt="checkbox-icon" />
        )}

        <SectionTitle style={{
          textDecoration: isCompleted ? 'line-through' : 'none',
          color: isCompleted ? 'black' : 'inherit'
        }}>
          {text}
        </SectionTitle>
      </Box>
      <img src={righticon} alt="right-icon" />
    </Box>
  );

  const handleItemClick = (index) => {
    // Open the corresponding popup but do NOT pre-mark the task. The popup's
    // own submit/save/confirm handler is the source of truth: SessionBooking
    // onSubmit → updateTaskCompletion(0), VerifyClasses onConfirm →
    // updateTaskCompletion(1), MarkingPreferences onSave →
    // updateTaskCompletion(2). Pre-marking on click meant closing without
    // submitting still persisted the task as done, which is wrong.
    switch (index) {
      case 0:
        setShowSessionBooking(true);
        break;
      case 1:
        setShowVerifyClassesPopup(true);
        break;
      case 2:
        setShowMarkingPreferencesPopup(true);
        break;
      default:
        break;
    }
  };

  const personaliseSection = (
    <TextContainer>
      <PersonaliseTitle>Personalise your marking experience</PersonaliseTitle>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {menuItems.map((item, index) => (
          <ListItemRow
            key={index}
            text={item}
            isCompleted={completedTasks[index]}
            onClick={() => handleItemClick(index)}
          />
        ))}
      </Box>
    </TextContainer>
  );

  const exploreSection = (
    <TextContainer>
      <PersonaliseTitle>Explore the platform</PersonaliseTitle>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {platformItems.map((item, index) => (
          <ListItemRow
            key={index}
            text={item}
            isCompleted={completedTasks[index + 3]}
            onClick={() => {
              // Mirror components under /onboarding/* carry the tour
              // overlays so production pages stay tour-free. The matching
              // checklist row is ticked by markTourAsComplete (see
              // TourContext's MARK_TOUR_COMPLETE reducer) on either the
              // tour's final step OR a Skip All — both count as the user
              // engaging with that feature, so we don't heckle them by
              // leaving the row open. We do NOT pre-mark the row on click
              // (cases 0-3); the completion signal comes from the tour
              // itself. Case 4 (View class insights) has no tour mirror,
              // so clicking it is the only completion signal — it gets
              // the immediate tick.
              // launchTour atomically activates exactly one tour at step 1 and
              // clears the other three (+ their steps), so no stale flag from a
              // previously-walked tour can leak alongside the new one.
              switch (index) {
                case 0:
                  launchTour('marking');
                  history.push("/onboarding/marking");
                  onClose();
                  break;
                case 1:
                  launchTour('draft');
                  setShowTeacherOnboarding(false);
                  history.push("/onboarding/documents/demo-draft?mode=demo");
                  onClose();
                  break;
                case 2:
                  launchTour('newTask');
                  history.push("/onboarding/new-task");
                  onClose();
                  break;
                case 3:
                  launchTour('feedback');
                  history.push("/onboarding/submissions/demo-onboarding?mode=demo");
                  onClose();
                  break;
                case 4:
                  // "View class insights" has no tour/mirror — it sends the
                  // teacher straight to the real Class Insights page. Ticking
                  // task[7] is the only completion signal. Then exit cleanly,
                  // exactly like exitToTasks: persistDismissal() flips the
                  // cached profile (hasCompletedOnboarding=true) so the
                  // checklist's auto-show effect in App.jsx doesn't immediately
                  // re-open on top of the insights page; onClose() hides the
                  // checklist; and we navigate to /classes-insights — the NEW
                  // ClassInsightsPage, NOT the old /classes list. TourGuard
                  // allow-lists /classes-insights while the checklist is open,
                  // so this sanctioned exit never pops the leave-tour warning
                  // (JED-1098).
                  updateTaskCompletion(7);
                  persistDismissal();
                  onClose();
                  history.push("/classes-insights");
                  break;
                default:
                  break;
              }
            }}
          />
        ))}
      </Box>
    </TextContainer>
  );

  return (
    <Container showPopup={showDraftFeedback}>
      {!showDraftFeedback && (
        <>
          {/* Context, not controls. The wrapper keeps its slot as the
              Container's first child (the styled first-child rules raise it
              to z-2005 above the takeover) while `inert` makes the whole
              header chrome non-interactive for BOTH pointer and keyboard —
              its New Task / bell / help / avatar were clickable above the
              overlay: New Task pushed /tasks/new (TourGuard allows it) while
              the checklist stayed mounted, a dead-end. `inert="true"` (any
              value = present = inert per the HTML boolean-attribute rules;
              React 18 forwards unknown attributes verbatim). staticBreadcrumbs
              renders a fixed "Complete setup" crumb instead of the
              route-derived crumbs of whatever page sits underneath. */}
          <div inert="true">
            <Header staticBreadcrumbs={[{ name: "Complete setup" }]} />
          </div>
          <ContentWrapper>
            <ModalContainer>
              <InnerWrapper style={remainingCount === 0 ? { gap: '32px' } : {}}>
                <HeaderWrapper>
                  <Title>{remainingCount === 0 ? "Setup Complete!" : "Complete your setup in less than 60 seconds"}</Title>
                  <CloseIcon src={closeicon} alt="close-icon" onClick={handleOnClose} />
                </HeaderWrapper>

                <ProgressContainer>
                  <SubText>
                    {remainingCount === 0
                      ? "Congratulations on completing all the steps. Time to create your first task"
                      : `Complete the last ${remainingCount} steps`}
                  </SubText>
                  {remainingCount === 0 ? (
                    <PurpleBackgroundBtn
                      buttonText="Create your first task"
                      onClickFn={exitToTaskCreation}
                    />
                  ) : (
                    <ProgressBar bgcolor="#7200E0" completed={progressPercentage} />
                  )}
                </ProgressContainer>

                {!isPersonaliseCompleted ? (
                  <>
                    {personaliseSection}
                    {exploreSection}
                  </>
                ) : (
                  <>
                    {exploreSection}
                    {personaliseSection}
                  </>
                )}
                {remainingCount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <PurpleBackgroundBtn buttonText="Skip all" onClickFn={handleOnClose} />
                  </div>
                )}
              </InnerWrapper>
            </ModalContainer>

          </ContentWrapper>
        </>
      )}

      {showSessionBooking && (
        <SessionBookingPopup
          menuItems={[
            "I have booked a session",
            "Someone else from my faculty has booked a session",
            "My faculty has already completed the session"
          ]}
          onClose={() => setShowSessionBooking(false)}
          onSubmit={async (selectedOption) => {
            // Only tick the task + advance the chain AFTER the save succeeds.
            // Previously updateTaskCompletion(0) ran outside the try, so a failed
            // save still marked the row done — persisting a green check the
            // backend never recorded.
            try {
              await saveSessionBooking(selectedOption);
            } catch (e) {
              console.error('Failed to save session booking', e);
              toast(<Toast message="Couldn't save that — please try again." />);
              return;
            }
            updateTaskCompletion(0);
            setShowSessionBooking(false);
            if (!completedTasks[1]) {
              setTimeout(() => setShowVerifyClassesPopup(true), 50);
            }
          }}
        />
      )}

      {showVerifyClassesPopup && (
        <VerifyClassesPopup
          setShowVerifyClassesPopup={setShowVerifyClassesPopup}
          onOpenClassChangeRequest={() => { setShowClassChangeRequest(true) }}
          setShowMarkingPreferencesPopup={(val) => {
            if (val && completedTasks[2]) return;
            setShowMarkingPreferencesPopup(val);
          }}
          onConfirm={() => updateTaskCompletion(1)}
        />
      )}

      {showClassChangeRequest && (
        <ClassChangeRequestModal
          onClose={() => setShowClassChangeRequest(false)}
        />
      )}

      {showMarkingPreferencesPopup && (
        <MarkingPreferencesPopup
          setShowMarkingPreferencesPopup={setShowMarkingPreferencesPopup}
          onSave={() => {
            // Saving marking preferences only marks the marking-prefs item (index 2).
            // It used to also flip tasks 0 and 1, which was wrong — saving prefs
            // doesn't mean the user booked a session or confirmed their classes.
            updateTaskCompletion(2);
            setShowMarkingPreferencesPopup(false);
          }}
        />
      )}

      {/* Confirmation popup before the user permanently dismisses the
          checklist. "Leave tour" fires the pendingExit callback (push to
          /tasks AND persist hasCompletedOnboarding=true on the backend so
          the next login doesn't re-show). "Stay in tour" just hides the
          warning and leaves the checklist intact. */}
      <TourExitWarning
        open={Boolean(pendingExit)}
        onCancel={() => setPendingExit(null)}
        onConfirm={() => {
          const fn = pendingExit;
          setPendingExit(null);
          if (typeof fn === 'function') fn();
        }}
        // This instance always guards the CHECKLIST close (X / Skip all).
        variant="checklist"
      />

    </Container>
  );
};

export default OnboardingComplete;
